import React, { useEffect, useState } from "react";
import { NotFoundText } from "../../../../components/common/styles";
import { Header } from "../header/Header";
import { useNavigate } from "react-router-dom";
import { ReactComponent as DotsActionsIcon } from "../../../../assets/icons/ThreeDotsVertical.svg";
import { useTranslation } from "react-i18next";
import { fetchTrainersStart } from "../../../../store/features/trainers/trainers-slice";
import { useAppDispatch, useAppSelector } from "../../../../store/config";
import { Pagination, Spin, notification } from "antd";
import { IMAGE_BASE_URL, CARDS_PAGE_SIZE } from "../../../../common/config";
import {
  CardActionsButton,
  StyledCard,
  StyledCardImage,
  StyledCardMeta,
  StyledCardMetaWrapper,
  StyledCardTableLayout,
} from "../../../../components/common/styles/tableStyles";
import { SortType } from "../../../../interfaces/screens/fetch-params-types";
import { useDebounce } from "../../../../hooks/useDebounce";
import { IFetchParams } from "../../../../interfaces/base";

export const TrainersTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [api, contextHolder] = notification.useNotification();

  const { trainers, loading, error, metadata, tableState } = useAppSelector(
    (state) => state.trainers
  );
  const { search, sort, pagination } = tableState;

  const [inputSearch, setInputSearch] = useState<string>(search);
  const debouncedSearch = useDebounce(inputSearch, 300);

  useEffect(() => {
    const paginationToUse =
      debouncedSearch !== search
        ? { page: 0, limit: CARDS_PAGE_SIZE, skip: 0 }
        : pagination;

    fetchTrainers({ pagination: paginationToUse });
  }, [debouncedSearch]);

  useEffect(() => {
    if (error) {
      openNotification();
    }
  }, [error]);

  const openNotification = () => {
    api["error"]({
      message: t("Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const onPaginationChange = (page: number) => {
    const newPagination = {
      ...pagination,
      page,
      skip: CARDS_PAGE_SIZE * (page - 1),
    };

    fetchTrainers({ pagination: newPagination });
  };

  const handleSetSort = (value: SortType) => {
    fetchTrainers({
      sort: value,
      pagination: { page: 0, limit: CARDS_PAGE_SIZE, skip: 0 },
    });
  };

  const fetchTrainers = (body: IFetchParams) => {
    dispatch(
      fetchTrainersStart({
        search: body.search || debouncedSearch,
        sort: body.sort || sort,
        pagination: body.pagination || pagination,
      })
    );
  };

  return (
    <>
      {contextHolder}
      <Header
        search={inputSearch}
        setSearch={setInputSearch}
        setSort={handleSetSort}
      />
      <Spin spinning={loading}>
        {trainers.length === 0 && !loading && (
          <NotFoundText>{t("No trainers found")}</NotFoundText>
        )}
        <StyledCardTableLayout>
          {trainers.map((trainer: any) => (
            <StyledCard
              key={trainer._id}
              cover={
                <StyledCardImage src={IMAGE_BASE_URL + "/" + trainer.avatar} />
              }
            >
              <CardActionsButton
                menu={{
                  items: [
                    {
                      key: "edit",
                      label: t("Edit"),
                      onClick: () =>
                        navigate(`/trainers/${trainer._id}`, {
                          state: { isEdit: true },
                        }),
                    },
                  ],
                }}
              >
                <DotsActionsIcon />
              </CardActionsButton>
              <StyledCardMetaWrapper
                onClick={() => navigate(`/trainers/${trainer._id}`)}
              >
                <StyledCardMeta
                  title={trainer.fullName}
                  description={
                    trainer.fitnessProfile[0]?.trainerData?.specializations
                      ?.map(t)
                      .join(", ") || ""
                  }
                />
              </StyledCardMetaWrapper>
            </StyledCard>
          ))}
        </StyledCardTableLayout>
        <Pagination
          current={metadata?.page}
          pageSize={CARDS_PAGE_SIZE}
          total={metadata?.total}
          align="center"
          hideOnSinglePage
          onChange={onPaginationChange}
          showSizeChanger={false}
          style={{ direction: "ltr", marginTop: "60px" }}
        />
      </Spin>
    </>
  );
};
